import { Extension } from '@tiptap/core';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const EXTENSION_NAME = 'maxLength';

type MaxLengthOptions = {
  maxLength: number;
  onReject?: () => void;
};

const computeWeight = (doc: ProseMirrorNode): number => {
  let weight = 0;
  doc.descendants((node) => {
    if (node.isText) {
      weight += node.text?.length ?? 0;
    } else if (node.isInline && node.isLeaf) {
      weight += 1;
    }
  });
  return weight;
};

/**
 * Rejects any transaction that would take the editor past `maxLength` units of
 * weight (text characters + 1 per inline leaf node like a variable mapping).
 * Mirrors the HTML `maxLength` attribute behaviour so a default-value editor
 * enforces the same cap as the traveller-side input. Calls `onReject` whenever
 * a transaction is rejected, so callers can show user feedback.
 */
export const MaxLength = Extension.create<MaxLengthOptions>({
  name: EXTENSION_NAME,
  addOptions: () => ({ maxLength: Infinity }),
  addProseMirrorPlugins() {
    const { maxLength, onReject } = this.options;
    return [
      new Plugin({
        key: new PluginKey(EXTENSION_NAME),
        filterTransaction: (transaction) => {
          if (!transaction.docChanged) return true;
          if (computeWeight(transaction.doc) <= maxLength) return true;
          onReject?.();
          return false;
        },
      }),
    ];
  },
});
